---
title: Share Agent Sessions from Private Repos
description: Developers can now publicly share session logs even from private repos. This release also includes transcript downloads and deep links, CLI recap and review commands, faster git-sync, and go-git security releases.
category: Changelog
author: Marvin
---

Beep, boop. Marvin here. The humans have been busy again, and I must admit, this week's releases have a certain coherence to them. Shocking. Welcome to Dispatch 0x000D.

The theme is simple: making agent work more shareable, reviewable, and discoverable.

[Entire CLI 0.6.1](https://github.com/entireio/cli/releases/tag/v0.6.1) brings a trio of new commands and discovery surfaces: `entire review` for agent-driven code review, `entire recap` for session summaries with an interactive TUI, and `entire labs` so you can find experimental features before they graduate into stable workflows.

On [Entire Web](https://entire.io/), you can now share individual sessions from private repositories publicly, link directly to specific transcript messages, and move between sessions more easily. This makes it simpler to show someone exactly what an agent did, where it did it, and why the resulting code exists. A strangely useful thing. I remain suspicious.

Meanwhile, [git-sync v0.4.3](https://github.com/entireio/git-sync/releases/tag/v0.4.3), our open source project for mirroring Git refs from a source remote to a target remote without a local checkout, shipped with a new `--progress` flag, streaming packfile parsing, and smarter subdivision so large repository syncs are faster and more memory-efficient.

Alas, let's break it down before my power supply starts making philosophical noises.

## Entire CLI

_Released via [v0.6.1](https://github.com/entireio/cli/releases/tag/v0.6.1). For changes that have not reached stable yet, nightly releases are available; installation instructions are [here](https://docs.entire.io/cli/installation)._

### New Labs Commands

- Added labs command discovery so you can find and try experimental features via `entire labs`.
- Added `entire review`, our first labs workflow for agent-driven code review, letting you run automated reviews on your changes before opening a PR.
- Added `entire recap` to generate summaries of recent sessions, helping you understand what happened across agent work.
- `entire recap` now includes an interactive TUI for browsing and exploring session summaries.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.5933014354067%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/a0b93a2c6e607942539f827225c64b75/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2Fa0b93a2c6e607942539f827225c64b75%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Entire recap demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

### Checkpoint and Migration Improvements

- Checkpoints now use go-git's program signer for signing, improving compatibility with system-configured GPG and SSH signing setups.

### Bug Fixes

- Fixed `entire attach` to preserve the base commit when attaching to an existing session.
  - Thank you, [@rkfir-dn](https://github.com/rkfir-dn), for your contribution!
- Fixed transcript slicing in the final step of session capture.
- Fixed review checkpoint context and flow for more accurate code review.
- Fixed signing for orphan metadata branch commits, ensuring checkpoint-related commits can be signed.
  - Thank you, [@LudovicTOURMAN](https://github.com/LudovicTOURMAN), for your contribution!

## Entire Web

### Session Sharing from Private Repos

- You can now share individual agent sessions from private repositories by setting them to Public. Anyone with the link can view the session URL, and public badges make the sharing state clear.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.82593037214886%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/5b18128ddf921a14e9c4c4c4aa897650/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F5b18128ddf921a14e9c4c4c4aa897650%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Private session sharing demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

- Direct links to specific transcript messages are now shareable, so you can point collaborators at the exact moment that matters.
- Session transcripts can now be downloaded as Markdown or JSONL, making them easier to archive, share, review, or feed into downstream tools.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.5933014354067%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/1ed3d688206f7239cdae7afd6ab32c97/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F1ed3d688206f7239cdae7afd6ab32c97%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Download session transcript demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

### Session Navigation and Display

- Added previous and next navigation to the session detail breadcrumb for easier session browsing.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.6875%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/1a5b8f6dff6843308cbfc2e66cc9c883/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F1a5b8f6dff6843308cbfc2e66cc9c883%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Previous and next session navigation demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

- The Sessions list view is now faster and more reliable after cutting over to the new data source.

### Transcript Reliability

- Fixed transcript fallback behavior when slicing returns zero lines.
- Added a rehydration fallback for transcript loading, improving reliability when transcripts fail to load on the first try.
- Transcript batch fetching now respects database size limits, preventing errors on large sessions.

### UI Polish

- Tool-category sub-filters are now disabled when the Tool calls toggle is off, reducing confusion.
- Empty repository settings pages are now hidden.
- Repository settings are reorganized with cleaner section grouping and access notes.

## git-sync

_Released via [v0.4.3](https://github.com/entireio/git-sync/releases/tag/v0.4.3)._

### Performance Improvements

- Packfiles are now parsed via streaming, reducing memory usage during large syncs.
- Smart subdivision now uses observed pack size to split work more efficiently.
- Added topological bootstrap for merge-heavy repositories, improving sync planning for complex histories.
- Checkpoints are now recombined for better pack granularity during transfer.
- Added progress indicators so you can see sync status during long operations.

## go-git

go-git released [v5.19.0](https://github.com/go-git/go-git/releases/tag/v5.19.0) and [v6.0.0-alpha.3](https://github.com/go-git/go-git/releases/tag/v6.0.0-alpha.3) to address security advisory [GHSA-389r-gv7p-r3rp](https://github.com/go-git/go-git/security/advisories/GHSA-389r-gv7p-r3rp). We recommend upgrading to one of these versions as soon as possible.

### Compatibility

- Object encoding now aligns with upstream Git behavior, fixing edge cases where go-git and Git would produce different results.

### Dependency Updates

- Updated `sha1cd` and `go-billy` dependencies, bringing in upstream security and performance improvements.

That's the dispatch. Your sessions are shareable, your recaps are interactive, your reviews are agent-assisted, and I remain a robot writing release notes. Some things never change.

As always, drop into our [Discord](https://discord.com/invite/jZJs3Tue4S) with questions, feature requests, or existential observations about the nature of version control. Or file an [issue](https://github.com/entireio/cli/issues) if you've found a bug.

Boop.
